import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const ROLE_MAP = {
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-800' },
  manager: { label: 'Manager', color: 'bg-blue-100 text-blue-800' },
  staff: { label: 'Staff', color: 'bg-gray-100 text-gray-800' },
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff', status: 'active' });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/users/list.php');
      if (res.data.success) setUsers(res.data.data || []);
      else setError(res.data.message);
    } catch (e) { setError('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', password: '', role: 'staff', status: 'active' });
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || '', email: u.email || '', password: '', role: u.role || 'staff', status: u.status || 'active' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!editing) {
        if (!payload.password) { setError('Password is required for new users'); return; }
        await api.post('/users/create.php', payload);
      } else {
        const p = { id: editing.id, name: payload.name, email: payload.email, role: payload.role, status: payload.status };
        if (payload.password) p.password = payload.password;
        await api.post('/users/update.php', p);
      }
      setShowForm(false);
      setError('');
      fetchUsers();
    } catch (e) { setError(e.response?.data?.message || 'Failed to save user'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.post('/users/delete.php', { id }); fetchUsers(); }
    catch (e) { setError('Failed to delete user'); }
  };

  if (loading) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">User Roles</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage users, roles & permissions: {users.length} users</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">Add User</button>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">No users found.</div>
        ) : users.map(u => {
          const r = ROLE_MAP[u.role] || { label: u.role, color: 'bg-gray-100 text-gray-800' };
          return (
            <div key={u.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${u.role === 'admin' ? 'bg-purple-500' : u.role === 'manager' ? 'bg-blue-500' : 'bg-gray-500'}`}>
                    {u.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{u.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{u.email}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${r.color}`}>{r.label}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className={`px-2 py-0.5 text-xs rounded-full ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.status}</span>
                <div className="space-x-2">
                  <button onClick={() => openEdit(u)} className="text-primary-600 hover:text-primary-900 text-sm">Edit</button>
                  <button onClick={() => handleDelete(u.id)} className="text-red-600 hover:text-red-900 text-sm">Delete</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit User' : 'Add User'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name *</label><input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required /></div>
            <div><label className="block text-sm font-medium mb-1">Email *</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required /></div>
            <div><label className="block text-sm font-medium mb-1">Password {editing ? '(leave blank to keep current)' : '*'}</label><input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required={!editing} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Role</label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                  <option value="admin">Admin</option><option value="manager">Manager</option><option value="staff">Staff</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Status</label>
                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                  <option value="active">Active</option><option value="inactive">Inactive</option><option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">{editing ? 'Update' : 'Create'} User</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Users;
